import {
  Stack,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import { 
  Add,
  ShowChart,
  Scale,
  TrendingUp,
  Edit,
  Delete,
} from '@mui/icons-material'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts'
import dayjs from 'dayjs'

const WeightTrackingTab = ({ 
  entry, 
  displayUnit, 
  setDisplayUnit, 
  chartTimePeriod, 
  setChartTimePeriod,
  onAddWeight,
  onEditWeight,
  onDeleteWeight,
  formatWeight,
  getUnitLabel,
  getChartData,
  calculateGrowthRate
}) => {
  const growthRate = calculateGrowthRate()

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Weight Tracking
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Display Unit</InputLabel>
            <Select
              value={displayUnit}
              label="Display Unit"
              onChange={(e) => setDisplayUnit(e.target.value)}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="oz">Ounces (oz)</MenuItem>
              <MenuItem value="g">Grams (g)</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onAddWeight}
          >
            Add Weight
          </Button>
        </Box>
      </Box>

      {/* Growth Chart Section */}
      {entry?.weights && entry.weights.length > 1 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChart color="primary" />
              Growth Chart
            </Typography>
            <ToggleButtonGroup
              value={chartTimePeriod}
              exclusive
              onChange={(_, newPeriod) => newPeriod && setChartTimePeriod(newPeriod)}
              size="small"
            >
              <ToggleButton value="1m">1M</ToggleButton>
              <ToggleButton value="3m">3M</ToggleButton>
              <ToggleButton value="6m">6M</ToggleButton>
              <ToggleButton value="1y">1Y</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis 
                  label={{ value: `Weight (${getUnitLabel(displayUnit)})`, angle: -90, position: 'insideLeft' }}
                />
                <ChartTooltip 
                  formatter={(value) => [`${value} ${getUnitLabel(displayUnit)}`, 'Weight']}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#2196F3" 
                  strokeWidth={2}
                  dot={{ fill: '#2196F3', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  name={`Weight (${getUnitLabel(displayUnit)})`}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Growth Rate Analysis */}
          {growthRate && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Growth Rate Analysis ({chartTimePeriod === 'all' ? 'All Time' : chartTimePeriod.toUpperCase()})
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Total Change</Typography>
                  <Typography variant="h6" color={growthRate.totalChange >= 0 ? 'success.main' : 'error.main'}>
                    {growthRate.totalChange >= 0 ? '+' : ''}{growthRate.totalChange.toFixed(2)} {getUnitLabel(displayUnit)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Daily Rate</Typography>
                  <Typography variant="h6">
                    {growthRate.dailyRate >= 0 ? '+' : ''}{growthRate.dailyRate.toFixed(3)} {getUnitLabel(displayUnit)}/day
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Weekly Rate</Typography>
                  <Typography variant="h6">
                    {growthRate.weeklyRate >= 0 ? '+' : ''}{growthRate.weeklyRate.toFixed(2)} {getUnitLabel(displayUnit)}/week
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Period</Typography>
                  <Typography variant="h6">
                    {Math.round(growthRate.period)} days
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}

      {entry?.weights && entry.weights.length > 0 ? (
        <>
          <Paper variant="outlined">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Recorded By</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entry.weights.map((weight, index) => {
                    const currentWeightGrams = weight.weightGrams || weight.weight * 453.592
                    const previousWeight = entry.weights[index + 1]
                    const previousWeightGrams = previousWeight ? (previousWeight.weightGrams || previousWeight.weight * 453.592) : null
                    const change = previousWeightGrams ? 
                      parseFloat(formatWeight(currentWeightGrams, displayUnit)) - parseFloat(formatWeight(previousWeightGrams, displayUnit)) 
                      : null
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          {dayjs(weight.date).format('M/D/YYYY')}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Scale fontSize="small" color="action" />
                            {formatWeight(currentWeightGrams, displayUnit)} {getUnitLabel(displayUnit)}
                          </Box>
                        </TableCell>
                        <TableCell>{weight.recordedBy}</TableCell>
                        <TableCell>
                          {change !== null && (
                            <Chip
                              icon={<TrendingUp />}
                              label={`${change > 0 ? '+' : ''}${change.toFixed(displayUnit === 'g' ? 0 : 2)} ${getUnitLabel(displayUnit)}`}
                              color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit weight">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => onEditWeight(index)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete weight">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => onDeleteWeight(index)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {entry.weights.length > 1 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Weight Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Current Weight</Typography>
                  <Typography variant="h6">
                    {formatWeight(entry.weights[0]?.weightGrams || entry.weights[0]?.weight * 453.592, displayUnit)} {getUnitLabel(displayUnit)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Starting Weight</Typography>
                  <Typography variant="h6">
                    {formatWeight(entry.weights[entry.weights.length - 1]?.weightGrams || entry.weights[entry.weights.length - 1]?.weight * 453.592, displayUnit)} {getUnitLabel(displayUnit)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Total Change</Typography>
                  <Typography 
                    variant="h6" 
                    color={(() => {
                      const current = parseFloat(formatWeight(entry.weights[0]?.weightGrams || entry.weights[0]?.weight * 453.592, displayUnit))
                      const start = parseFloat(formatWeight(entry.weights[entry.weights.length - 1]?.weightGrams || entry.weights[entry.weights.length - 1]?.weight * 453.592, displayUnit))
                      return current > start ? 'success.main' : current < start ? 'error.main' : 'text.primary'
                    })()}
                  >
                    {(() => {
                      const current = parseFloat(formatWeight(entry.weights[0]?.weightGrams || entry.weights[0]?.weight * 453.592, displayUnit))
                      const start = parseFloat(formatWeight(entry.weights[entry.weights.length - 1]?.weightGrams || entry.weights[entry.weights.length - 1]?.weight * 453.592, displayUnit))
                      const change = current - start
                      return `${change > 0 ? '+' : ''}${change.toFixed(displayUnit === 'g' ? 0 : 2)} ${getUnitLabel(displayUnit)}`
                    })()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="body2" color="text.secondary">Records</Typography>
                  <Typography variant="h6">{entry.weights.length}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Scale sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Weight Records
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Start tracking this bird&apos;s weight to monitor growth and health.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onAddWeight}
          >
            Add First Weight
          </Button>
        </Paper>
      )}
    </Stack>
  )
}

export default WeightTrackingTab